import { useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useAppDispatch } from 'src/store/store';
import { authApi, useGetAuthContextQuery, useLazyGetAuthContextQuery } from 'src/services/auth/auth.service';

import { SplashScreen } from 'src/components/loading-screen';

import { TenantType } from 'src/types/enums';

import { logOut } from '../../utils/auth/utils';
// ----------------------------------------------------------------------

export const AccessTokenKey = 'access_token';

type Props = {
  children: React.ReactNode;
};

export default function SubscriptionReviewGuard({ children }: Props) {

  const { isLoading } = useGetAuthContextQuery(undefined);

  return <>{isLoading ? <SplashScreen /> : <Container>{children}</Container>}</>;
}

// ----------------------------------------------------------------------

function Container({ children }: Props) {
  const router = useRouter();

  const dispatch = useAppDispatch();

  const { data, isLoading, isError, refetch } = useGetAuthContextQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: 30,
  });

  const [getAuthContext] = useLazyGetAuthContextQuery();

  useEffect(() => {
    if (isError) {
      dispatch(authApi.util.invalidateTags(['AuthContext']));
    }
  }, [dispatch, isError]);

  const [checked, setChecked] = useState(false);

  const check = useCallback(async () => {
    if (isLoading) return;

    if (isError) {
      // if the user is not authenticated, log them out
      logOut();
    }

    if (data) {
      const { user, account, organization } = data;

      if ([TenantType.Client, TenantType.Recruiter].includes(organization.type)) {
        // if billing review is required, send the user to the billing review pages
        if (organization.billing_review_required) {

          // recheck to get latest auth context
          const { data: newAuthContext } = await getAuthContext();

          if (newAuthContext?.organization.billing_review_required) {
            router.replace(paths.setup.organization.billing.choosePlan);
          }
        }
      }
    }

    setChecked(true);
  }, [isLoading, data, isError, getAuthContext, router]);

  useEffect(() => {
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isError]);

  if (!checked) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
